import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';

/**
 * The event passed to `onOpen`/`onClose`. Real DOM/synthetic events are forwarded
 * untouched (so consumers can inspect `event.type` / `event.target` — see `infoIcon`'s
 * PROD-1751 blur guard); the `{ type, target }` arm is a fallback for synthetic sources.
 */
export type TooltipChangeEvent =
  | ReactMouseEvent
  | ReactFocusEvent
  | Event
  | { type: string; target: EventTarget | null };

type AnchorListeners = {
  onMouseEnter?: (event: ReactMouseEvent) => void;
  onMouseLeave?: (event: ReactMouseEvent) => void;
  onFocus?: (event: ReactFocusEvent) => void;
  onBlur?: (event: ReactFocusEvent) => void;
  onClick?: (event: ReactMouseEvent) => void;
};

type PopperListeners = {
  onMouseEnter?: (event: ReactMouseEvent) => void;
  onMouseLeave?: (event: ReactMouseEvent) => void;
};

type UseTooltipParams = {
  /** When a boolean, the tooltip is controlled and render follows it. */
  open?: boolean;
  onOpen?: (event: TooltipChangeEvent) => void;
  onClose?: (event: TooltipChangeEvent) => void;
  enterDelay: number;
  leaveDelay: number;
  disableHoverListener: boolean;
  disableFocusListener: boolean;
  disableTouchListener: boolean;
  disableInteractive: boolean;
  isTouchDevice: boolean;
};

export type UseTooltipResult = {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  /** Stable callback ref to attach to the anchor (merge with the child's own ref). */
  setAnchorRef: (node: HTMLElement | null) => void;
  /** Event handlers to merge onto the cloned anchor element. */
  anchorListeners: AnchorListeners;
  /** Hover-bridge handlers to merge onto the popup element (keeps it open on hover). */
  popperListeners: PopperListeners;
  /** Passed to the `ClickAwayListener` wrapping the popup — dismisses on outside press. */
  handleClickAway: (event: MouseEvent | TouchEvent) => void;
};

type TimerRef = { current: ReturnType<typeof setTimeout> | undefined };

const clearTimer = (timer: TimerRef): void => {
  if (timer.current) {
    clearTimeout(timer.current);
    timer.current = undefined;
  }
};

/**
 * Interaction state machine for {@link Tooltip}.
 *
 * Touch vs pointer: on touch devices we attach **no hover/focus listeners** (so the
 * emulated `mouseover` a tap produces can't re-open a just-closed tooltip — the old
 * MUI Tooltip's mobile-persistence bug) and toggle on the synthesized **click**
 * instead. Relying on `click` lets the browser's own tap detection absorb the finger
 * jitter that manual `touchmove` tracking would mistake for a drag (taps silently
 * dropped on Android Chrome). Dismissal is delegated to a `ClickAwayListener` plus
 * Escape and (touch) scroll. Nothing auto-hides on a timer, so a tapped tooltip can be
 * read at leisure (WCAG 1.4.13). `enterDelay`/`leaveDelay` apply to hover only.
 *
 * Controlled vs uncontrolled: when `open` is a boolean the parent owns visibility and
 * internal state never flips, but enabled listeners still fire `onOpen`/`onClose` so
 * controlled consumers (`infoIcon`, `fabHelpButton`) keep working. When every listener
 * is disabled (the `menuCell` persistent tooltip) no handlers/timers are attached at all.
 */
export const useTooltip = ({
  open,
  onOpen,
  onClose,
  enterDelay,
  leaveDelay,
  disableHoverListener,
  disableFocusListener,
  disableTouchListener,
  disableInteractive,
  isTouchDevice,
}: UseTooltipParams): UseTooltipResult => {
  const isControlled = open !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = isControlled ? (open as boolean) : uncontrolledOpen;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const enterTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Latest values, read inside delayed callbacks to avoid stale closures.
  const latest = useRef({ isControlled, onOpen, onClose, isOpen });
  latest.current = { isControlled, onOpen, onClose, isOpen };

  const fireOpen = useCallback((event: TooltipChangeEvent): void => {
    latest.current.onOpen?.(event);
    if (!latest.current.isControlled) {
      setUncontrolledOpen(true);
    }
  }, []);

  const fireClose = useCallback((event: TooltipChangeEvent): void => {
    latest.current.onClose?.(event);
    if (!latest.current.isControlled) {
      setUncontrolledOpen(false);
    }
  }, []);

  const scheduleOpen = (event: TooltipChangeEvent): void => {
    clearTimer(leaveTimer);
    clearTimer(enterTimer);
    if (enterDelay > 0) {
      enterTimer.current = setTimeout(() => fireOpen(event), enterDelay);
    } else {
      fireOpen(event);
    }
  };

  const scheduleClose = (event: TooltipChangeEvent): void => {
    clearTimer(enterTimer);
    clearTimer(leaveTimer);
    if (leaveDelay > 0) {
      leaveTimer.current = setTimeout(() => fireClose(event), leaveDelay);
    } else {
      fireClose(event);
    }
  };

  // Primary anchor source: the child's ref. Stable so React doesn't re-attach each render.
  const setAnchorRef = useCallback((node: HTMLElement | null): void => {
    setAnchorEl(node);
  }, []);

  // Fallback anchor source: the element that received an open-intent event. Only fills
  // when the ref didn't (e.g. children that don't forward refs, like `NumberBadge`).
  const captureAnchor = (element: HTMLElement | null): void => {
    if (element) {
      setAnchorEl((previous) => previous ?? element);
    }
  };

  const anchorListeners: AnchorListeners = {};

  if (!isTouchDevice && !disableHoverListener) {
    anchorListeners.onMouseEnter = (event) => {
      captureAnchor(event.currentTarget as HTMLElement);
      scheduleOpen(event);
    };
    anchorListeners.onMouseLeave = (event) => {
      scheduleClose(event);
    };
  }

  if (!isTouchDevice && !disableFocusListener) {
    anchorListeners.onFocus = (event) => {
      // Only open for keyboard focus, not the focus a mouse/touch click leaves behind.
      const target = event.target as HTMLElement;
      try {
        if (
          typeof target.matches === 'function' &&
          !target.matches(':focus-visible')
        ) {
          return;
        }
      } catch {
        // jsdom can't evaluate :focus-visible — fall through and open.
      }
      captureAnchor(event.currentTarget as HTMLElement);
      scheduleOpen(event);
    };
    anchorListeners.onBlur = (event) => {
      scheduleClose(event);
    };
  }

  if (isTouchDevice && !disableTouchListener) {
    // Toggle on the synthesized click — the browser's tap detection tolerates the
    // finger jitter that manual touchstart/touchmove tracking misreads as a drag.
    anchorListeners.onClick = (event) => {
      captureAnchor(event.currentTarget as HTMLElement);
      if (latest.current.isOpen) {
        fireClose(event);
      } else {
        fireOpen(event);
      }
    };
  }

  const popperListeners: PopperListeners = {};
  if (!isTouchDevice && !disableInteractive && !disableHoverListener) {
    popperListeners.onMouseEnter = () => {
      // Pointer moved from anchor onto the popup — cancel the pending close.
      clearTimer(leaveTimer);
    };
    popperListeners.onMouseLeave = (event) => {
      scheduleClose(event);
    };
  }

  const handleClickAway = (event: MouseEvent | TouchEvent): void => {
    if (!latest.current.isOpen) {
      return;
    }
    const target = event.target as Node | null;
    // A press on the anchor toggles via its own onClick — don't also close here.
    if (anchorEl && target && anchorEl.contains(target)) {
      return;
    }
    fireClose(event);
  };

  // Escape (everywhere) and scroll (touch) dismissal while open. Outside-press is
  // handled declaratively by the ClickAwayListener in the component.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        fireClose(event);
      }
    };
    document.addEventListener('keydown', onKeyDown);

    let onScroll: ((event: Event) => void) | undefined;
    if (isTouchDevice) {
      onScroll = (event) => fireClose(event);
      window.addEventListener('scroll', onScroll, {
        capture: true,
        passive: true,
      });
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (onScroll) {
        window.removeEventListener('scroll', onScroll, { capture: true });
      }
    };
  }, [isOpen, isTouchDevice, fireClose]);

  // Never leave a timer running past unmount (the persistent `menuCell` tooltip
  // mounts/unmounts frequently).
  useEffect(
    () => () => {
      clearTimer(enterTimer);
      clearTimer(leaveTimer);
    },
    [],
  );

  return {
    isOpen,
    anchorEl,
    setAnchorRef,
    anchorListeners,
    popperListeners,
    handleClickAway,
  };
};
